import React, { useRef, useEffect } from 'react';
import { useHistory } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { API } from '../../utils/amplifyWrapper';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { Dropdown, DropdownItem, DropdownDivider } from '../ui/bootstrap';
import {
    Play, Code2, ExternalLink, Edit3, Download, Link as LinkIcon,
    Copy, Trash2, Upload, Podcast, Power, PowerOff
} from 'lucide-react';
import formatAudioFileDuration from '../../utils/AudioDuration';
import { useOrganisation } from '../../http-hooks/organisation/useOrganisation';
import { useGlobalStore } from '../../zustand-store/useGlobalStore';
import { addMixPanelEvent } from '../../store/actions';
import { DOWNLOAD_AUDIO_CLICKED, DOWNLOAD_SUCCEEDED, DOWNLOAD_BLOCKED, EMBED_CODE_CLICKED, SHARE_CLICKED } from '../../constants/mixpanel-events';
import { AUDIO_DOWNLOADED } from '../../constants/gtag-events';
import { trackGA4Event } from '../../utils/trackGA4Event';
import { C } from '../../styles/tokens';
import { WaveThumb } from '../ui/Icons';

// Kept for header/shimmer alignment in Content.js
export const CONTENT_TABLE_COLS = '1fr 90px 110px 100px 64px';

function isDraft({ full_audio_url }) {
    return `${full_audio_url}`.includes('/null');
}

const AudioStatusBadge = ({ isAudioDisabled, fullAudioStatus, publishedAt }) => {
    const base = {
        borderRadius: 999, fontSize: 10.5, padding: '3px 8px',
        fontWeight: 600, display: 'inline-block',
    };
    if (isAudioDisabled)
        return <span style={{ ...base, background: '#FEE2E2', color: '#DC2626' }}>Disabled</span>;
    if (fullAudioStatus === 'IN PROGRESS')
        return <span style={{ ...base, background: C.p100, color: C.p700 }}>In Progress</span>;
    if (publishedAt === undefined || publishedAt)
        return <span style={{ ...base, background: C.g50, color: C.g600 }}>Published</span>;
    return <span style={{ ...base, background: C.a50, color: C.a500 }}>Draft</span>;
};

const ContentItem = ({
    item, index, org_id, id, isLast,
    setAudio, setAudioTitle, setShowUploadAudio, setSelectedAudio,
    setShowEmbedCode, setEmbedCode, setShowPodcast, setContentId,
    disableAudio, enableAudio, requestDelete,
    // CustomToggle kept for API compatibility but unused in table layout
    CustomToggle,
}) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { data: organisation } = useOrganisation({ org_id });
    const toggleUpgradeModal = useGlobalStore((state) => state.toggleUpgradeModal);
    const showUpgradeModal = useGlobalStore((state) => state.showUpgradeModal);
    const contentId = item.SK.split("#")[1];

    // Once-per-attempt lock: set synchronously on the first Download click so the burst of
    // rapid re-clicks fired before the blocking upgrade overlay paints don't each emit
    // DOWNLOAD_AUDIO_CLICKED. Released when the attempt ends (modal closed / request settled).
    const downloadLockRef = useRef(false);

    // Blocked path releases here: the gate opens the upgrade modal (a blocking overlay), so the
    // attempt is over once the user dismisses it and can try again.
    useEffect(() => {
        if (!showUpgradeModal) downloadLockRef.current = false;
    }, [showUpgradeModal]);

    const isUploadedAudio = !(item.body) && !(item.slate_body);
    const isInProgress = item.full_audio_status === 'IN PROGRESS';
    const hasDraft = isDraft(item);
    const hasAudio = item.published_at !== null && item.full_audio_url;

    const cloneContent = () => {
        API.post('main', `/organisation/${org_id}/integration/${id}/content/clone`, {
            body: { content_to_clone: contentId },
            headers: { "Content-Type": "application/json" },
        }).then((response) => {
            if (response.success) {
                history.push(`/organisation/${org_id}/integration/${id}/content/${response.data.content_id}`);
            }
        }).catch((error) => {
            toast.error("Something went wrong while cloning content.");
            Sentry.captureException(error);
        });
    };

    const handleRowClick = () => {
        if (!isUploadedAudio) {
            history.push(`/organisation/${org_id}/integration/${id}/content/${contentId}`);
        } else {
            setShowUploadAudio(true);
            setSelectedAudio(item);
        }
    };

    const handleDownload = () => {
        // Ignore repeat clicks while an attempt is already in flight (see downloadLockRef).
        if (downloadLockRef.current) return;
        downloadLockRef.current = true;

        // GROW-85: the content-list download serves the full published audio.
        const export_format = 'mp3_voice_music';
        const plan_id = organisation?.plan_id;
        const canDownload = organisation?.gates?.can_download_audio;

        dispatch(addMixPanelEvent({
            orgId: org_id, integrationId: id, contentId, body: {
                eventName: DOWNLOAD_AUDIO_CLICKED,
                eventData: {
                    source: 'content_list',
                    export_format,
                    can_download: canDownload,
                    plan_id,
                    duration_sec: item.audio_duration,
                },
            },
        }));

        if (!canDownload) {
            // Permission outcome: blocked by the plan gate before any request.
            dispatch(addMixPanelEvent({
                orgId: org_id, integrationId: id, contentId, body: {
                    eventName: DOWNLOAD_BLOCKED,
                    eventData: { source: 'content_list', export_format, plan_id, reason: 'plan_gate' },
                },
            }));
            // Lock stays held until the modal closes (released by the effect above).
            toggleUpgradeModal(true, { title: "Upgrade to get unlimited downloads, more credits and more features.", source: 'download_gate', isManual: false });
            return;
        }
        API.get('main', `/organisation/${org_id}/integration/${id}/content/${contentId}/download`)
            .then((resp) => {
                if (!resp.success) {
                    // Permission outcome: server-side gate denied the download.
                    dispatch(addMixPanelEvent({
                        orgId: org_id, integrationId: id, contentId, body: {
                            eventName: DOWNLOAD_BLOCKED,
                            eventData: { source: 'content_list', export_format, plan_id, reason: 'server_gate' },
                        },
                    }));
                    toggleUpgradeModal(true, { title: resp.message, source: 'download_gate', isManual: false });
                    return;
                }
                // Permission outcome: allowed and delivered.
                dispatch(addMixPanelEvent({
                    orgId: org_id, integrationId: id, contentId, body: {
                        eventName: DOWNLOAD_SUCCEEDED,
                        eventData: { source: 'content_list', export_format, plan_id, duration_sec: item.audio_duration },
                    },
                }));
                // GROW-69: audio_downloaded previously only fired from the editor's export
                // flow — the content-list download (the path most users actually use) never
                // reported to GA4 at all.
                trackGA4Event(AUDIO_DOWNLOADED, { format: 'mp3_voice_music', duration_sec: item.audio_duration });
                // Free/preview plans get a limited number of downloads; refresh the org so
                // gates.can_download_audio reflects the download just consumed.
                queryClient.invalidateQueries(['organisation', org_id]);
                window.location.href = resp.download_url;
            })
            .catch((error) => { Sentry.captureException(error); toast.error("Something went wrong."); })
            // Success path releases here once the request settles. (When resp.success is false
            // the modal opens and the effect above releases instead — harmless double-reset.)
            .finally(() => { downloadLockRef.current = false; });
    };

    const handleEmbed = () => {
        dispatch(addMixPanelEvent({
            orgId: org_id, integrationId: id, contentId, body: {
                eventName: EMBED_CODE_CLICKED,
                eventData: {},
            },
        }));

        setShowEmbedCode(true);
        setEmbedCode(`<iframe
id="l2it-widget-iframe"
src="${process.env.REACT_APP_WIDGET_BASE_URL}/inline.html?org_id=${org_id}&integration_id=${id}&${item.url ? `url=${encodeURIComponent(item.url)}` : `content_id=${contentId}`}&has_paywall=false"
style="border:none;width:100%;height:80px;"
>
</iframe>`);
    };

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleRowClick}
            onKeyDown={e => e.key === 'Enter' && handleRowClick()}
            className="content-table-row"
            style={{
                padding: '10px 16px',
                borderBottom: isLast ? 'none' : `1px solid ${C.n100}`,
                background: 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                alignItems: 'center',
                transition: 'background .1s',
                outline: 'none',
            }}
            onMouseEnter={e => e.currentTarget.style.background = C.n50}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
            {/* Title + thumbnail */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <div style={{
                    width: 32, height: 32, background: C.p50, borderRadius: 7,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    {isUploadedAudio
                        ? <Upload size={14} color={C.p500} strokeWidth={1.75} />
                        : <WaveThumb />
                    }
                </div>
                <div style={{ minWidth: 0 }}>
                    <div style={{
                        fontSize: 13.5, fontWeight: 600, color: C.n900, marginBottom: 1,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {item.title}
                    </div>
                    <div style={{ fontSize: 10.5, color: C.n400 }}>
                        {isInProgress ? 'Processing…' : isUploadedAudio ? 'Uploaded' : 'Voiceover'}
                    </div>
                </div>
            </div>

            {/* Duration */}
            <div className="ct-col-duration" style={{ fontSize: 12.5, color: C.n600 }}>
                {item.audio_duration ? formatAudioFileDuration(item.audio_duration) : '—'}
            </div>

            {/* Created */}
            <div className="ct-col-created" style={{ fontSize: 12.5, color: C.n500 }}>
                {item.created_at ? moment(item.created_at).format("MMM D, YYYY") : '—'}
            </div>

            {/* Status */}
            <div>
                <AudioStatusBadge
                    publishedAt={item.published_at}
                    fullAudioStatus={item.full_audio_status}
                    isAudioDisabled={item.is_audio_disabled}
                />
            </div>

            {/* Actions: play + ··· */}
            <div
                style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}
                onClick={e => e.stopPropagation()}
            >
                {hasAudio && (
                    <button
                        onClick={() => { if (!isInProgress) { setAudio(item.full_audio_url); setAudioTitle(item.title); } }}
                        disabled={isInProgress}
                        style={{
                            width: 26, height: 26, borderRadius: 6,
                            background: C.p50, border: `1px solid ${C.p200}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: isInProgress ? 'default' : 'pointer',
                            opacity: isInProgress ? 0.5 : 1, padding: 0,
                        }}
                    >
                        <Play size={11} color={C.p600} fill={C.p600} strokeWidth={0} />
                    </button>
                )}

                <Dropdown
                    trigger={
                        <button style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: C.n400, fontSize: 17, lineHeight: 1, padding: '2px 4px',
                            borderRadius: 4, letterSpacing: 1,
                        }}>
                            ···
                        </button>
                    }
                    align="end"
                >
                    <DropdownItem onClick={handleRowClick}>
                        <Edit3 size={13} />Edit
                    </DropdownItem>
                    {!isUploadedAudio && (
                        <DropdownItem onClick={cloneContent}>
                            <Copy size={13} />Duplicate
                        </DropdownItem>
                    )}
                    {hasAudio && !isInProgress && (
                        <>
                            <DropdownItem onClick={handleDownload}>
                                <Download size={13} />Download
                            </DropdownItem>
                            <DropdownItem onClick={handleEmbed}>
                                <Code2 size={13} />Embed code
                            </DropdownItem>
                            <DropdownItem onClick={() => {
                                dispatch(addMixPanelEvent({
                                    orgId: org_id, integrationId: id, contentId, body: {
                                        eventName: SHARE_CLICKED,
                                        eventData: {},
                                    },
                                }));
                                window.open(
                                    `${process.env.REACT_APP_WIDGET_BASE_URL}/inline.html?org_id=${org_id}&integration_id=${id}&${item.url ? `url=${encodeURIComponent(item.url)}` : `content_id=${contentId}`}`,
                                    '_blank'
                                );
                            }}>
                                <ExternalLink size={13} />Share
                            </DropdownItem>
                        </>
                    )}
                    {item.url && (
                        <DropdownItem onClick={() => window.open(item.url, '_blank')}>
                            <LinkIcon size={13} />Article link
                        </DropdownItem>
                    )}
                    {null === item.published_at ? (
                        hasDraft && (
                            <DropdownItem onClick={() => requestDelete(item, index)} danger>
                                <Trash2 size={13} />Delete
                            </DropdownItem>
                        )
                    ) : (
                        <>
                            {item.is_audio_disabled ? (
                                <DropdownItem onClick={() => enableAudio(contentId, index)}>
                                    <Power size={13} />Enable Audio
                                </DropdownItem>
                            ) : (
                                item.url && (
                                    <DropdownItem onClick={() => disableAudio(contentId, index)}>
                                        <PowerOff size={13} />Disable Player Widget
                                    </DropdownItem>
                                )
                            )}
                            {/* Podcast feature removed from product — kept commented in case it returns
                            <DropdownItem onClick={() => { setShowPodcast(true); setContentId(contentId); }}>
                                <Podcast size={13} />Add to podcast feed
                            </DropdownItem>
                            */}
                            <DropdownDivider />
                            <DropdownItem onClick={() => requestDelete(item, index)} danger>
                                <Trash2 size={13} />Delete
                            </DropdownItem>
                        </>
                    )}
                </Dropdown>
            </div>
        </div>
    );
};

export default ContentItem;
